/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import { trueFalseDefaultOptions } from "../formDefinitionFunctions";
import { UseForm } from "../../../../types/UseForm";
import personFields from "../modules/personFields";
import addressFields from "../modules/addressFields";
import * as stagesCase from "../../../Stages/StagesCase";
import _ from "lodash";
import {
  authorityReferenceDefault,
  beaSafeIdFieldDefault,
  dateFieldDefault,
  emailDefault,
  faxNumberDefault,
  instituteReferenceDefault,
} from "../../../validationRules";
import { FormElementPropsWithoutProduct } from "../../../../components/Case/CaseForm/FormElement";
import InputAdornment from "@mui/material/InputAdornment";
import React from "react";
import basicCaseInsuranceFields from "../modules/basicCaseInsuranceFields";
import { getCommonContractLawCaseRejectionCategorySelectOptions } from "../contract/contractLawFormHelperService";
import DateChangeButtonsToolbar from "../../../../components/Validator/DateChangeButtonsToolbar";
import { userHasOneOfTheseRoles } from "../../../backofficeUserService";
import { BackofficeUser, Roles } from "../../../../types/BackofficeUser";
import { extrajudicialInvoicingFields, judicialInvoicingFields } from "./abstractLitigationInvoicingFields";
import { translate } from "../../../Translations/translatorService";
import { SelectOption } from "../../../../types/FormDefinitions/FullFormDefinition";
import { LitigationUnion } from "../../../../types/LitigationUnion";
import { courtPostalCodeAutoCompleteAdditionalProps } from "../../../formFieldsDefaultDefinitions";
import { hasActiveCourtAppointment } from "../../../courtAppointmentService";
import { allDataPageCourtFields } from "../allDataPageCourtFields";

const defaultExtrajudicialRecommendationOptionsFactory = (product: LitigationUnion): SelectOption[] => {
  return product.caseCategory === "defendClaim"
    ? [
        { value: "agreementDone" },
        { value: "insufficientEvidence" },
        { value: "insufficientChancesOfSuccess" },
        { value: "costBenefitConsiderations" },
        { value: "lawsuit" },
        { value: "individualText" },
      ]
    : [
        { value: "agreementDone" },
        { value: "insufficientEvidence" },
        { value: "insufficientChancesOfSuccess" },
        { value: "costBenefitConsiderations" },
        { value: "anonymousOpponent" },
        { value: "opponentUnableToPay" },
        { value: "lawsuit" },
        { value: "individualText" },
      ];
};

export const aiConcernDescription = (product: LitigationUnion) => {
  return {
    isHidden: ({ product }: { product: LitigationUnion }) => product.stage! < stagesCase.STAGE_DATA_COMPLETED,
    label: "Beschreibung der Situation",
    elements: [
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.finalClientDescriptionAndObjectives`,
        isDisabled: () => true,
        additionalProps: {
          multiline: true,
          fullWidth: true,
        },
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.initialClientDescriptionAndObjectives`,
        isDisabled: () => true,
        additionalProps: {
          multiline: true,
          fullWidth: true,
        },
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.refinementAnswers`,
        isDisabled: () => true,
        additionalProps: {
          multiline: true,
          fullWidth: true,
        },
      },
    ],
  };
};

export const getUnderageClientForm = (product: LitigationUnion, label: string) => {
  return {
    label: label,
    elements: [
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.clientWasMinorAtContractDate`,
        options: () => trueFalseDefaultOptions(`${product.productClassName}.clientWasMinorAtContractDate`),
        additionalProps: {
          fullWidth: true,
        },
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.clientLegalRepresentativeAmount`,
        options: () => [{ value: 1 }, { value: 2 }],
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values[product.productClassName].clientWasMinorAtContractDate !== true,
        additionalProps: {
          fullWidth: true,
        },
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.clientLegalRepresentativesAuthorizationType`,
        options: () => [{ value: "both" }, { value: "individually" }],
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values[product.productClassName].clientWasMinorAtContractDate !== true ||
          values[product.productClassName].clientLegalRepresentativeAmount < 2,
        additionalProps: {
          fullWidth: true,
        },
      },
      ...personFields({
        path: `${product.productClassName}.clientLegalRepresentative`,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values[product.productClassName].clientWasMinorAtContractDate !== true,
      }),
      ...addressFields({
        path: `${product.productClassName}.clientLegalRepresentative.residenceAddress`,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values[product.productClassName].clientWasMinorAtContractDate !== true,
      }),
      ...personFields({
        path: `${product.productClassName}.secondClientLegalRepresentative`,
        isHidden: ({ values }: { values: UseForm["values"] }) => {
          return (
            values[product.productClassName].clientWasMinorAtContractDate !== true ||
            values[product.productClassName].clientLegalRepresentativeAmount < 2
          );
        },
      }),
      ...addressFields({
        path: `${product.productClassName}.secondClientLegalRepresentative.residenceAddress`,
        isHidden: ({ values }: { values: UseForm["values"] }) => {
          return (
            values[product.productClassName].clientWasMinorAtContractDate !== true ||
            values[product.productClassName].clientLegalRepresentativeAmount < 2
          );
        },
      }),
    ],
  };
};

export const getAdditionalClientsForm = (product: LitigationUnion) => ({
  label: "Weitere Mandantschaft",
  isHidden: () => false,
  elements: [
    {
      type: "AdditionalClientsForm",
      path: `${product.productClassName}.additionalClients`,
      additionalProps: {
        fullWidth: true,
      },
    },
  ],
});

export const getNextInstanceSection = (product: LitigationUnion) => ({
  label: "Urteilsprüfung",
  isHidden: ({ product }: { product: LitigationUnion }) => product.stage! < stagesCase.STAGE_EVALUATE_JUDGEMENT,
  elements: [
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.nextInstanceRecommendation`,
      isDisabled: ({ product }: { product: LitigationUnion }) => product.stage! !== stagesCase.STAGE_EVALUATE_JUDGEMENT,
      options: () => [{ value: "appeal" }, { value: "noAppeal" }],
      alert: {
        onChange:
          "Mit der Auswahl einer Option wird sofort und automatisch eine Anfrage an die Mandantschaft gesendet. Daher bitte sicherstellen, dass die Empfehlung final ist.",
        onInitial:
          "Mit der Auswahl einer Option wird sofort und automatisch eine Anfrage an die Mandantschaft gesendet. Daher bitte sicherstellen, dass die Empfehlung final ist.",
      },
    },
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.nextInstanceDecisionClient`,
      helperText:
        "Achtung: Feld ist über Servicewelt durch Mandantschaft zu befüllen! Wenn dieses Feld von uns befüllt wird, " +
        "kann die Mandantschaft kein Feedback mehr abgeben. Daher nur befüllen, wenn mit Mandantschaft explizit abgesprochen.",
      alert: {
        onInitial:
          "Sie möchten den Wert im Feld “Entscheidung Mandantschaft nächste Instanz” erstmalig ändern. Dieses " +
          "Feld wird standardmäßig durch die Mandantschaft über eine Anfrage in der Servicewelt befüllt und sollte " +
          "nur im Ausnahmefall und nach ausdrücklicher Abstimmung mit der Mandantschaft manuell befüllt werden! " +
          "Mit der Auswahl einer Option und anschließendem Speichern wird der Fall in die Stage Rechtsstreit beendet " +
          "verschoben. Sofern Sie “Berufung einlegen” auswählen, wird ein neuer Fall angelegt und darin ggf. eine " +
          "Deckungsanfrage an die Versicherung gesendet. Daher bitte sicherstellen, dass die Entscheidung final ist.",
        onChange:
          "Sie möchten den Wert im Feld “Entscheidung Mandantschaft nächste Instanz” erstmalig ändern. Dieses " +
          "Feld wird standardmäßig durch die Mandantschaft über eine Anfrage in der Servicewelt befüllt und sollte " +
          "nur im Ausnahmefall und nach ausdrücklicher Abstimmung mit der Mandantschaft manuell befüllt werden! " +
          "Mit der Auswahl einer Option und anschließendem Speichern wird der Fall in die Stage Rechtsstreit beendet " +
          "verschoben. Sofern Sie “Berufung einlegen” auswählen, wird ein neuer Fall angelegt und darin ggf. eine " +
          "Deckungsanfrage an die Versicherung gesendet. Daher bitte sicherstellen, dass die Entscheidung final ist.",
      },
      isDisabled: ({ product }: { product: LitigationUnion }) =>
        !_.includes([stagesCase.STAGE_LAWSUIT_COMPLETED, stagesCase.STAGE_EVALUATE_JUDGEMENT], product.stage),
      options: () => [{ value: "appeal" }, { value: "checkJudgement" }, { value: "noAppeal" }],
    },
    {
      type: "ValidatorDateField",
      validators: dateFieldDefault,
      path: `${product.productClassName}.promulgationOfJudgementDate`,
    },
    {
      type: "ValidatorDateField",
      validators: dateFieldDefault,
      path: `${product.productClassName}.judgementEebSentDate`,
    },
  ],
});

export const opponentLawyer = (product: LitigationUnion) => {
  return {
    label: "Anwalt Gegenseite",
    isHidden: ({ product }: { product: LitigationUnion }) =>
      (product.stage || 0) < stagesCase.STAGE_DATA_COMPLETED || product.advisoryHandling,
    elements: [
      {
        type: "BeaLawyerImportButton",
        path: "pathPropNotUsedButRequired",
        additionalProps: {
          fullWidth: true,
        },
      },
      ...personFields({ path: `${product.productClassName}.opponentLawyer`, allowNoGender: false }),
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opponentLawFirm.organization.name`,
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opponentLawFirm.organization.mainEmail.email`,
        validators: emailDefault,
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opponentLawFirm.organization.telephone`,
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opponentLawFirm.organization.fax`,
        validators: faxNumberDefault,
        additionalProps: {
          InputProps: {
            startAdornment: <InputAdornment position="start">+49</InputAdornment>,
          },
        },
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opponentLawFirm.organization.beaSafeId`,
        validators: beaSafeIdFieldDefault,
      },
      ...addressFields({ path: `${product.productClassName}.opponentLawFirm.organization.address` }),
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.referenceOpponentLawyer`,
      },
    ],
  };
};

export const prosecution = (product: LitigationUnion) => {
  return {
    label: "Staatsanwaltschaft",
    isHidden: ({ product }: { product: LitigationUnion }) =>
      (product.stage || 0) < stagesCase.STAGE_DATA_COMPLETED || product.advisoryHandling,
    elements: [
      {
        type: "ProsecutionPicker",
        path: `${product.productClassName}.responsibleProsecution`,
        additionalProps: {
          fullWidth: true,
        },
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.prosecutionReference`,
        validators: instituteReferenceDefault,
        additionalProps: {
          fullWidth: true,
        },
      },
    ],
  };
};

export const processParticipantsAuthority = (product: LitigationUnion) => {
  return {
    label: "Behörde",
    elements: [
      {
        type: "AuthorityPicker",
        path: `${product.productClassName}.responsibleAuthority`,
        additionalProps: {
          fullWidth: true,
        },
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.authorityReference`,
        validators: authorityReferenceDefault,
        isHidden: ({ product }: { product: LitigationUnion }) => product.responsibleAuthority === null,
      },
    ],
  };
};

export const processParticipantsPeople = (product: LitigationUnion) => {
  return {
    label: "Personen",
    isHidden: ({ product }: { product: LitigationUnion }) => product.processParticipantsPeople.length < 1,
    elements: [
      {
        type: "ProcessParticipantsPeopleForm",
        path: `${product.productClassName}.relatedPeople`,
        additionalProps: {
          fullWidth: true,
        },
      },
    ],
  };
};

export const addProcessParticipant = () => {
  return {
    label: "Neu hinzufügen",
    isHidden: () => false,
    elements: [
      {
        type: "AddProcessParticipantForm",
        path: "addProcessParticipant", // just here because its also used as key for react
        additionalProps: {
          fullWidth: true,
        },
      },
    ],
  };
};

export const processParticipantsOrganizations = (product: LitigationUnion) => {
  return {
    label: "Unternehmen",
    isHidden: ({ product }: { product: LitigationUnion }) => product.processParticipantsOrganizations.length < 1,
    elements: [
      {
        type: "ProcessParticipantsOrganizationsForm",
        path: `${product.productClassName}.relatedOrganizations`,
        additionalProps: {
          fullWidth: true,
        },
      },
    ],
  };
};

export const caseInsurance = (product: LitigationUnion) => {
  return {
    label: "Versicherung",
    isHidden: ({ product }: { product: LitigationUnion }) => product.paymentType !== "insurance",
    elements: [
      ...basicCaseInsuranceFields({
        path: `${product.productClassName}.caseInsurance`,
        product: product,
      }),
      {
        type: "ValidatorNumberField",
        path: `${product.productClassName}.caseInsurance.deductible`,
        fieldType: "money",
        numberType: "float",
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.caseInsurance.extrajudicialCoverageRequestedDate`,
        validators: dateFieldDefault,
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.caseInsurance.extrajudicialCoverageRequestAnsweredDate`,
        validators: dateFieldDefault,
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.caseInsurance.extrajudicialCoverageGranted`,
        options: () => trueFalseDefaultOptions(`${product.productClassName}.extrajudicialCoverageGranted`),
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.caseInsurance.judicialCoverageRequestedDate`,
        validators: dateFieldDefault,
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.caseInsurance.judicialCoverageRequestAnsweredDate`,
        validators: dateFieldDefault,
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.caseInsurance.judicialCoverageGranted`,
        options: () => trueFalseDefaultOptions(`${product.productClassName}.extrajudicialCoverageGranted`),
      },
    ],
  };
};

export const opposingParty = (product: LitigationUnion) => {
  return {
    isHidden: ({ product }: { product: LitigationUnion }) =>
      product.stage! < stagesCase.STAGE_DATA_COMPLETED || product.caseCategory === "advisory",
    label: "Gegenseite",
    elements: [
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.opposingPartyType`,
        isDisabled: ({ product }: { product: LitigationUnion }) => product.stage! >= stagesCase.STAGE_COMPLETED,
        options: () => [{ value: "opposingPartyLegalEntity" }, { value: "opposingPartyPerson" }],
        additionalProps: {
          fullWidth: true,
        },
      },
      {
        type: "ContractualPartnerLegalEntityPicker",
        path: `${product.productClassName}.contractualPartnerLegalEntity.organization`,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values[product.productClassName].opposingPartyType !== "opposingPartyLegalEntity",
        additionalProps: {
          fullWidth: true,
        },
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingPartyReference`,
        helperText: translate(`${product.productClassName}.opposingPartyReference.helperText`),
        isDisabled: ({ product }: { product: LitigationUnion }) => product.stage < stagesCase.STAGE_DATA_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values[product.productClassName].opposingPartyType !== "opposingPartyLegalEntity",
      },
      ...personFields({
        path: `${product.productClassName}.contractualPartnerPerson`,
        allowNoGender: true,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values[product.productClassName].opposingPartyType !== "opposingPartyPerson",
        isDisabled: ({ product }: { product: LitigationUnion }) => product.stage! >= stagesCase.STAGE_COMPLETED,
      }),
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.contractualPartnerPerson.email`,
        validators: emailDefault,
        isDisabled: ({ product }: { product: LitigationUnion }) => product.stage! >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values[product.productClassName].opposingPartyType !== "opposingPartyPerson",
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.contractualPartnerPerson.telephone`,
        isDisabled: ({ product }: { product: LitigationUnion }) => product.stage! >= stagesCase.STAGE_COMPLETED,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values[product.productClassName].opposingPartyType !== "opposingPartyPerson",
      },
      ...addressFields({
        path: `${product.productClassName}.contractualPartnerPerson.residenceAddress`,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          values[product.productClassName].opposingPartyType !== "opposingPartyPerson",
        isDisabled: ({ product }: { product: LitigationUnion }) => product.stage! >= stagesCase.STAGE_COMPLETED,
      }),
    ],
  };
};

export const opposingLiabilityInsurance = (product: LitigationUnion) => {
  return {
    label: "Haftpflichtversicherung Gegenseite",
    elements: [
      {
        type: "OpposingLiabilityInsurancePicker",
        path: `${product.productClassName}.opposingLiabilityInsurance.organization`,
        additionalProps: {
          fullWidth: true,
        },
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingLiabilityInsurancePolicyNumber`,
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.opposingLiabilityInsuranceCaseReferenceNumber`,
      },
    ],
  };
};

export const extraJudicialProcedure = <T extends LitigationUnion = LitigationUnion>(
  product: T,
  currentUser: BackofficeUser,
  additionalElements: FormElementPropsWithoutProduct<T>[] = [],
  caseRejectionCategoryOptions = getCommonContractLawCaseRejectionCategorySelectOptions,
  extrajudicialRecommendationOptionsFactory: (
    product: T,
  ) => SelectOption[] = defaultExtrajudicialRecommendationOptionsFactory,
) => {
  return {
    label: "Außergerichtliches Verfahren",
    isHidden: ({ product }: { product: T }) =>
      product.stage < stagesCase.STAGE_DATA_COMPLETED ||
      product.caseCategory === "advisory" ||
      product.previousInstanceCase,
    elements: [
      {
        type: "CaseRejectionCategorySelect",
        path: `${product.productClassName}.caseRejectionCategory`,
        options: caseRejectionCategoryOptions,
        isDisabled: ({ product }: { product: T }) => product.stage >= stagesCase.STAGE_CHECKED,
        isHidden: ({ product, values }: { product: LitigationUnion; values: UseForm["values"] }) =>
          values[product.productClassName].caseCategoryAssessment !== "reject",
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.assessmentRejectionText`,
        isDisabled: ({ product }: { product: T }) => product.stage >= stagesCase.STAGE_CHECKED,
        isHidden: ({ product, values }: { product: LitigationUnion; values: UseForm["values"] }) =>
          values[product.productClassName].caseCategoryAssessment !== "reject",
        additionalProps: {
          multiline: true,
          rows: 12,
          fullWidth: true,
        },
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.extrajudicialCommunicationSent`,
        validators: dateFieldDefault,
        isDisabled: ({ product }: { product: T }) =>
          product.stage < stagesCase.STAGE_DATA_COMPLETED || product.stage >= stagesCase.STAGE_LAWSUIT,
        isHidden: ({ product }: { product: T }) => product.advisoryHandling,
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.extrajudicialCommunicationDeadline`,
        validators: dateFieldDefault,
        isDisabled: ({ product }: { product: T }) =>
          product.stage < stagesCase.STAGE_DATA_COMPLETED || product.stage >= stagesCase.STAGE_LAWSUIT,
        isHidden: ({ product }: { product: T }) => product.advisoryHandling,
        additionalProps: {
          toolbar: DateChangeButtonsToolbar,
        },
      },
      {
        type: "ValidatorNumberField",
        path: `${product.productClassName}.claimedAmountLawFirm`,
        isDisabled: ({ product }: { product: T }) => product.stage >= stagesCase.STAGE_CHECKED,
        fieldType: "money",
        numberType: "float",
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.extrajudicialReactionReceived`,
        validators: dateFieldDefault,
        isDisabled: ({ product }: { product: T }) =>
          product.extrajudicialReaction === "noReaction" ||
          product.stage < stagesCase.STAGE_EXTRAJUDICIAL_COMMUNICATION_SENT ||
          product.stage >= stagesCase.STAGE_EXTRAJUDICIAL_REACTION_RECEIVED,
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.extrajudicialReaction`,
        translationPath: `${product.productClassName}.extrajudicialReaction.${product.caseCategory}`,
        isDisabled: ({ product }: { product: T }) =>
          product.stage < stagesCase.STAGE_EXTRAJUDICIAL_COMMUNICATION_SENT ||
          product.stage >= stagesCase.STAGE_EXTRAJUDICIAL_REACTION_RECEIVED,
        options: () => [
          { value: "noReaction" },
          { value: "fullyAgreed" },
          { value: "partiallyAgreed" },
          { value: "disagreed" },
        ],
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.hasMonitoringBilling`,
        isHidden: ({ product }: { product: T }) =>
          !_.includes([2], product.caseInsurance.insurer?.id) && !product.caseInsurance.iIWPI,
        options: () => trueFalseDefaultOptions(`${product.productClassName}.hasMonitoringBilling`),
        isDisabled: ({ product }: { product: T }) =>
          product.stage < stagesCase.STAGE_CHECKED ||
          (product.stage === stagesCase.STAGE_COMPLETED &&
            !userHasOneOfTheseRoles(currentUser, [Roles.accounting, Roles.admin])),
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.extrajudicialRecommendation`,
        isDisabled: ({ product }: { product: T }) =>
          product.stage !== stagesCase.STAGE_EXTRAJUDICIAL_REACTION_RECEIVED &&
          product.stage !== stagesCase.STAGE_EXTRAJUDICIAL_COMMUNICATION_SENT,
        isHidden: ({ product }: { product: T }) => !_.includes(["defendClaim", "enforceClaim"], product.caseCategory),
        options: ({ product }: { product: T }) => extrajudicialRecommendationOptionsFactory(product),
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.extrajudicialRecommendationText`,
        isDisabled: ({ product }: { product: T }) =>
          !(
            product.stage === stagesCase.STAGE_EXTRAJUDICIAL_COMMUNICATION_SENT ||
            (product.stage === stagesCase.STAGE_EXTRAJUDICIAL_REACTION_RECEIVED &&
              product.extrajudicialRecommendationText)
          ),
        isHidden: ({ product }: { product: T }) =>
          !_.includes(["defendClaim", "enforceClaim"], product.caseCategory) ||
          (product.stage > stagesCase.STAGE_EXTRAJUDICIAL_REACTION_RECEIVED &&
            !product.extrajudicialRecommendationText),
        additionalProps: {
          multiline: true,
          fullWidth: true,
        },
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.finalClaimStatusClient`, // Forderungsstatus nach Fristende aus Mandantensicht
        translationPath: `${product.productClassName}.finalClaimStatusClient.${product.caseCategory}`, // Forderungsstatus nach Fristende aus Mandantensicht
        helperText:
          "Achtung: Feld ist über Servicewelt durch Mandantschaft zu befüllen! Wenn dieses Feld von uns befüllt wird, " +
          "kann die Mandantschaft kein Feedback mehr abgeben. Daher nur befüllen, wenn mit Mandantschaft explizit abgesprochen.",
        isDisabled: ({ product }: { product: T }) => product.stage !== stagesCase.STAGE_EXTRAJUDICIAL_REACTION_RECEIVED,
        isHidden: ({ product }: { product: T }) => product.advisoryHandling,
        options: () => [
          { value: "fullyAgreed" },
          { value: "partiallyAgreed" },
          { value: "notAgreed" },
          { value: "noReaction" },
        ],
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.finalProcessFeedbackClient`, // Rückmeldung Mandant
        translationPath: `${product.productClassName}.finalProcessFeedbackClient.${product.caseCategory}`, // Rückmeldung Mandant
        helperText:
          "Achtung: Feld ist über Servicewelt durch Mandantschaft zu befüllen! Wenn dieses Feld von uns befüllt wird, " +
          "kann die Mandantschaft kein Feedback mehr abgeben. Daher nur befüllen, wenn mit Mandantschaft explizit abgesprochen.",
        isDisabled: ({ product }: { product: T }) => product.stage !== stagesCase.STAGE_EXTRAJUDICIAL_REACTION_RECEIVED,
        isHidden: ({ product }: { product: T }) =>
          product.advisoryHandling || product.finalClaimStatusClient === "fullyAgreed",
        options: () => [{ value: "agreement" }, { value: "lawsuit" }],
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.statementOfClaimSentDate`,
        validators: dateFieldDefault,
        isHidden: ({ product }: T) => product.caseCategory !== "enforceClaim",
      },
      ...additionalElements,
      ...extrajudicialInvoicingFields<T>({ product }),
    ],
  };
};

export const judicialProcedure = <T extends LitigationUnion = LitigationUnion>(
  product: T,
  additionalElements: FormElementPropsWithoutProduct<T>[] = [],
): {
  label: string;
  isHidden: ({ product }: { product: T }) => boolean;
  elements: FormElementPropsWithoutProduct<T>[];
} => {
  return {
    label: "Gerichtliches Verfahren",
    isHidden: ({ product }) => product.stage < stagesCase.STAGE_DATA_COMPLETED || product.advisoryHandling,
    elements: [
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.paymentOrderDate`,
        validators: dateFieldDefault,
        isDisabled: ({ product }) => product.stage >= stagesCase.STAGE_LAWSUIT_COMPLETED,
        isHidden: ({ product }) => product.caseCategory !== "defendClaim",
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.enforcementOrderDate`,
        validators: dateFieldDefault,
        isDisabled: ({ product }) => product.stage >= stagesCase.STAGE_LAWSUIT_COMPLETED,
        isHidden: ({ product }) => product.caseCategory !== "defendClaim",
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.statementOfClaimFiledDate`,
        validators: dateFieldDefault,
        isDisabled: ({ product }) => product.stage >= stagesCase.STAGE_LAWSUIT_COMPLETED,
        isHidden: ({ product }) => product.caseCategory !== "defendClaim",
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.statementOfDefenseFiledDate`,
        validators: dateFieldDefault,
        isDisabled: ({ product }) => product.stage !== stagesCase.STAGE_LAWSUIT,
        isHidden: ({ product }) =>
          product.caseCategory !== "enforceClaim" ||
          ["lawsuitRevocation", "lawsuitRevocationBeforeHearing", "courtOrder"].includes(
            product.lawsuitCompletedReason,
          ),
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.replicaSentDate`,
        validators: dateFieldDefault,
        isDisabled: ({ product }) => product.stage !== stagesCase.STAGE_LAWSUIT,
        isHidden: ({ product }) =>
          product.caseCategory !== "enforceClaim" ||
          ["lawsuitRevocation", "lawsuitRevocationBeforeHearing", "courtOrder"].includes(
            product.lawsuitCompletedReason,
          ),
      },
      {
        type: "ValidatorAutoComplete",
        path: `${product.productClassName}.courtPostalCode`,
        additionalProps: courtPostalCodeAutoCompleteAdditionalProps,
        helperText:
          "Bitte hier die Postleitzahl eintragen, die für den Gerichtsstand ermittelt wurde und NICHT die Postleitzahl des Gerichtes.",
        isHidden: ({ product }) =>
          product.stage > stagesCase.STAGE_LAWSUIT ||
          product.caseCategory === "defendClaim" ||
          product.isHigherInstanceCase,
        alert: {
          onChange:
            "Das Ändern der Postleitzahl führt dazu, dass das zuständige Gericht erneut ermittelt wird. Möchten Sie die Postleitzahl tatsächlich ändern?",
        },
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.courtReference`,
        validators: instituteReferenceDefault,
        isHidden: ({ product }) => !_.includes(["defendClaim", "enforceClaim"], product.caseCategory),
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.statementOfDefenseSentDate`,
        validators: dateFieldDefault,
        isDisabled: ({ product }) => product.stage !== stagesCase.STAGE_LAWSUIT,
        isHidden: ({ product }) =>
          product.caseCategory !== "defendClaim" ||
          ["lawsuitRevocation", "lawsuitRevocationBeforeHearing", "courtOrder"].includes(
            product.lawsuitCompletedReason,
          ),
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.replicaFiledDate`,
        validators: dateFieldDefault,
        isDisabled: ({ product }) => product.stage !== stagesCase.STAGE_LAWSUIT,
        isHidden: ({ product }) =>
          product.caseCategory !== "defendClaim" ||
          ["lawsuitRevocation", "lawsuitRevocationBeforeHearing", "courtOrder"].includes(
            product.lawsuitCompletedReason,
          ),
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.lawsuitCompletedReason`,
        isDisabled: ({ product }) => product.stage < stagesCase.STAGE_LAWSUIT && product.stage > stagesCase.STAGE_TRIAL,
        isHidden: ({ product }) => product.advisoryHandling,
        options: ({ product }) => {
          const options = [
            { value: "courtDecision" },
            { value: "settlement" },
            { value: "lawsuitRevocation" },
            { value: "lawsuitRevocationBeforeHearing" },
            { value: "resolution" },
            { value: "defaultJudgement" },
            { value: "judgementAppeal" },
            { value: "judgementByConfession" },
          ];

          if (product.isHigherInstanceCase) {
            options.push({ value: "courtOrder" });
          }

          return options;
        },
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.customerLawsuitCompletedApprovalDate`,
        translationPath: ({ values }) =>
          `${product.productClassName}.customerLawsuitCompletedApprovalDate.${
            values[product.productClassName].lawsuitCompletedReason
          }`,
        validators: dateFieldDefault,
        isDisabled: ({ product }) => product.stage > stagesCase.STAGE_LAWSUIT,
        isHidden: ({ values }) =>
          _.includes(
            ["courtDecision", "defaultJudgement", "judgementAppeal", "judgementByConfession"],
            values[product.productClassName].lawsuitCompletedReason,
          ) || !values[product.productClassName].lawsuitCompletedReason,
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.lawsuitCompletedDate`,
        validators: dateFieldDefault,
        isDisabled: ({ product }: { product: T }) => product.stage > stagesCase.STAGE_EVALUATE_JUDGEMENT,
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          !values[product.productClassName].lawsuitCompletedReason,
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.judgementReceivedDate`,
        validators: dateFieldDefault,
        isDisabled: ({ product }) => product.stage >= stagesCase.STAGE_LAWSUIT_COMPLETED,
        isHidden: ({ product }) => product.stage < stagesCase.STAGE_LAWSUIT,
      },
      ...additionalElements,
      ...judicialInvoicingFields({ product }),
    ],
  };
};

export const litigationCourt = (product: LitigationUnion, courtTypes: string[]) => ({
  elements: [
    {
      type: "CourtPicker",
      path: `${product.productClassName}.responsibleCourt`,
      isDisabled: ({ product }: { product: LitigationUnion }) =>
        (product.caseCategory === "enforceClaim" && (product.stage || 0) < stagesCase.STAGE_ACTION_REQUIRED) ||
        hasActiveCourtAppointment(product.courtAppointments),
      additionalProps: {
        fullWidth: true,
        courtTypes,
      },
    },
    ...allDataPageCourtFields(product.productClassName, "responsibleCourt"),
  ],
});
